import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ActionButton from 'components/Widget/ActionButton';
import { ExportDsAiPreset } from 'dragonmouse-shared/api/export';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';

interface Props {
	label: string;
	preset: ExportDsAiPreset;
	componentNames?: string[];
	tokenDomains?: string[];
	customPrompt?: string;
}

const ExportDsAiAction = ({
	label,
	preset,
	componentNames,
	tokenDomains,
	customPrompt,
}: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const activeDesignSystem = useSelector((r: RootReducer) =>
		selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId)
	);

	const onClick = async () => {
		if (isLoading) return;
		if (!activeDesignSystem) return;

		const filename = buildExportFilename(
			activeDesignSystem.title,
			preset,
			'design-system',
			'md'
		);
		setIsLoading(true);
		try {
			await runExportWithToast({
				pendingLabel:
					preset === 'custom'
						? 'Writing your .md — this may take a minute or two…'
						: 'Preparing your .md…',
				errorLabel: 'Export failed. Please try again.',
				run: async () => {
					const { exportId } = await forge.exportDsAi({
						designSystemId: activeDesignSystem.id,
						preset,
						componentNames: preset === 'components' ? componentNames : undefined,
						tokenDomains: preset === 'tokens' ? tokenDomains : undefined,
						customPrompt: preset === 'custom' ? customPrompt : undefined,
						filename,
					});
					return { exportId, filename };
				},
			});
		} catch (e) {
			Sentry.captureException(e);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ActionButton
			icon={IconType.DOWNLOAD}
			label={label}
			loadingLabel="Preparing…"
			isLoading={isLoading}
			onClick={onClick}
		/>
	);
};

export default ExportDsAiAction;
